import React, { createContext, useContext, useState, useLayoutEffect } from 'react';
import { connect } from 'react-redux';
import Forward from '../../common/logic/claim/forward.js';
import saveUserData from '../../common/logic/claim/save_user_data.js';
import PageLoader from '../../common/components/page_loader.js';
import Intro from '../../common/components/intro.js';
import Screening from '../../common/components/screening.js';
import Qualified from '../../common/components/qualified.js';
import CustomQualified from '../../common/components/custom_qualified.js';
import ProductIntro from '../../common/components/product_intro.js';
import GroupIntro from '../../common/components/group_intro.js';
import Group from '../../common/components/metrics/group.js';
import ThoughtBubbleIntro from '../../common/components/thought_bubble_intro.js';
import ThoughtBubble from '../../common/components/metrics/thought_bubble.js';
import Markup from '../../common/components/metrics/markup.js';
import { fraudContext } from "../../common/contexts";
import { LastStepsComponents } from '../../common/logic/last_steps';
import { customMetricsByLocation } from "../../common/logic/eligible";
import WithOpenEnd from '../../common/components/metrics/customMetricsOpenEnd/withOpenEnd';
import runPreload from '../../common/logic/claim/preload';

const resultContext = createContext(
  {
    survey_status: '',
    answers: [],
    loops: [],
    claim_answers: [],
    claim_views: [],
    thought_bubbles: []
  }
);

const Index = ({
  data, updateStep, updateQuestionIndex, updateGroupIndex,
  updateRandomClaimIndex, qIndex, rcIndex, gIndex, step,
  loading, changeLoadingState
}) => {
  const {
    demographics, optional_demographics, screenings,
    claims_groups, markup_tool, translations, image_url,
    task, save_url, random_claims, qualified_custom_text
  } = data;

  const markupTool = markup_tool;
  const productUrl = image_url;
  const productText = task;

  const result = useContext(resultContext);
  const { projectId, antiFraud, fraudProcessing, gaTrackingId } = useContext(fraudContext);

  const saveRequest = (status) => {
    saveUserData(save_url, status, result, data);
  };

  const [ forwardEntity ] = useState(new Forward({
    updateStep,
    runSave: (status) => saveRequest(status),
    updateQuestionIndex,
    updateGroupIndex,
    updateRandomClaimIndex
  }
  ));

  const currentQuestion = (questions) => (questions[qIndex]);
  const currentRandomClaim = () => (random_claims[rcIndex]);
  const currentGroup = () => (claims_groups[gIndex]);
  const goForward = () => {
    forwardEntity.forward(step, data, result, projectId, antiFraud, gaTrackingId);
  };
  const goNextQuestion = (questions) => {
    forwardEntity.nextQuestion(
      qIndex, questions, result, goForward
    );
  };
  const goNextGroup = () => {
    forwardEntity.nextGroup(gIndex, claims_groups, goForward);
  };
  const goNextRandomClaim = () => {
    forwardEntity.nextRandomClaim(rcIndex, random_claims, goForward);
  };
  const maleSelected = () => (result.answers.findIndex("S3_1") > -1);

  useLayoutEffect(() => {
    runPreload(data, changeLoadingState);
  }, []);

  return (
    <>
      <PageLoader loading={ loading || fraudProcessing } />
      <section className="survey-layout">
        {
          !step &&
          <Intro
            task={ translations.base_intro }
            nextStep={ goForward }
          />
        }
        {
          step === 2 &&
          <Screening
            question={ currentQuestion(demographics) }
            nextStep={ () => { goNextQuestion(demographics); } }
            result={ result }
          />
        }
        {
          step === 3 &&
          <Screening
            question={ currentQuestion(optional_demographics) }
            nextStep={ () => { goNextQuestion(optional_demographics); } }
            result={ result }
          />
        }
        {
          step === 4 &&
          <Screening
            question={ currentQuestion(screenings) }
            nextStep={ () => { goNextQuestion(screenings); } }
            result={ result }
          />
        }
        {
          step === 5 && !qualified_custom_text &&
          <Qualified
            title={ translations.express_qualify_title }
            description={ translations.express_qualify_description }
            descriptionFooter={ translations.express_qualify_footer }
            nextStep={ goForward }
          />
        }
        {
          step === 5 && !!qualified_custom_text &&
          <CustomQualified
            content={ qualified_custom_text }
            nextStep={ goForward }
          />
        }
        {
          step === 6 &&
          <WithOpenEnd
            question={ currentQuestion(customMetricsByLocation(data.survey_metrics, "at_the_start")) }
            nextStep={ () => { goNextQuestion(customMetricsByLocation(data.survey_metrics, "at_the_start")); } }
            result={ result }
            mainResult={ result }
          />
        }
        {
          step === 7 &&
          <ProductIntro
            title={ translations.product_intro_sequential }
            url={ productUrl }
            text={ productText }
            nextStep={ goForward }
          />
        }
        {
          step === 8 &&
          <GroupIntro
            title={ translations.intro_maxdiff_title }
            description={ translations.intro_maxdiff_description }
            footer={ translations.intro_maxdiff_footer }
            nextStep={ goForward }
          />
        }
        {
          step === 9 &&
          <Group
            desktopTitle={ translations.maxdiff_groups_desktop }
            mobileTitle={ translations.maxdiff_groups_mobile }
            group={ currentGroup() }
            mostLabel={ translations.maxdiff_group_most }
            counter={ translations.maxdiff_group_middle.replace('%{n}', gIndex + 1).replace('%{k}', claims_groups.length) }
            leastLabel={ translations.maxdiff_group_least }
            result={ result }
            nextStep={ () => { goNextGroup(); } }
          />
        }
        {
          step === 10 &&
          <WithOpenEnd
            question={ currentQuestion(customMetricsByLocation(data.survey_metrics, "default")) }
            nextStep={ () => { goNextQuestion(customMetricsByLocation(data.survey_metrics, "default")); } }
            result={ result }
            mainResult={ result }
          />
        }
        {
          step === 11 &&
          <ThoughtBubbleIntro
            top={ translations.maxdiff_thought_bubble_intro_top.replace('%{n}', random_claims.length) }
            nextStep={ goForward }
          />
        }
        {
          step === 12 &&
          <ThoughtBubble
            concept={ currentRandomClaim() }
            tbKlass={ data.claim_klass }
            title={ translations.thought_bubble_title.replace('%currentFlavor%', currentRandomClaim().text) }
            description={ translations.thought_bubble_description }
            showMan={ maleSelected() }
            result={ result }
            nextStep={ () => { goNextRandomClaim(); } }
          />
        }
        {
          step === 13 &&
          <Markup
            conceptIds={ markupTool.concept_ids }
            markupTool={ markupTool }
            nextStep={ goForward }
            result={ result }
          />
        }
        {
          step === 14 &&
          <WithOpenEnd
            question={ currentQuestion(customMetricsByLocation(data.survey_metrics, "at_the_end")) }
            nextStep={ () => { goNextQuestion(customMetricsByLocation(data.survey_metrics, "at_the_end")); } }
            result={ result }
            mainResult={ result }
          />
        }
        <LastStepsComponents
          step={ step }
          lastStepsFrom={ 15 }
          translations={ translations }
          data={ data }
          nextStep={ goForward }
        />
      </section>
    </>
  );
};

const mapStateToProps = (state) => ({ ...state });

const mapDispatchToProps = (dispatch) => {
  return {
    updateStep: (step) => { return dispatch({ type: "UPDATE_STEP", value: step }); },
    changeLoadingState: (value) => { return dispatch({ type: "UPDATE_LOADING", value }); },
    updateGroupIndex: (cIndex) => { return dispatch({ type: "UPDATE_GROUP_INDEX", value: cIndex }); },
    updateRandomClaimIndex: (tIndex) => { return dispatch({ type: "UPDATE_RANDOM_CLAIM_INDEX", value: tIndex }); },
    updateQuestionIndex: (qIndex) => { return dispatch({ type: "UPDATE_QUESTION_INDEX", value: qIndex }); }
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(Index);
